import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GuardianType,
  UpdateGuardianType
} from 'apps/user/types/on-boarding.type';
import { GuardianTypeEnum } from 'apps/user/types/user.enum.type';
import { AxiosError } from 'axios';
import { Alert } from 'components/Toast';
import { toast } from 'react-toastify';
import { services } from 'service';
import { Guardian } from 'service/dto/guardian.dto';

interface IGuardianProps {
  update: UpdateGuardianType;
  guardianId: number;
}

export const useCreateGuardian = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (guardian: GuardianType): Promise<void> => {
      return services.guardianService.createGuardian(guardian);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['create-guardian']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useUpdateGuardian = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ guardianId, update }: IGuardianProps): Promise<void> => {
      return services.guardianService.updateTrustee(guardianId, update);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['update-trustee']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetGuardianList = ({
  enabled = true,
  search = undefined
}: {
  search?: string;
  enabled?: boolean;
} = {}) => {
  const searchQuery = search && search?.length >= 3 ? search : '';
  return useQuery(
    ['guardians'],
    () => services.guardianService.getGuardians(searchQuery),
    {
      refetchOnWindowFocus: false,
      enabled: enabled
    }
  );
};

export const useDeleteGuardian = () =>
  useMutation(
    (guardianId: number) => services.guardianService.deleteGuardian(guardianId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useAssignGuardian = () =>
  useMutation(
    ({
      guardianId,
      childrenIds,
      type
    }: {
      guardianId: number;
      childrenIds: number[];
      type: GuardianTypeEnum;
    }) =>
      services.guardianService.assignGuardian(guardianId, childrenIds, type),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useGetGuardian = ({ guardianId }: { guardianId: number }) => {
  return useQuery<Guardian, AxiosError<Error>>(
    ['get_guardian', guardianId],
    () => services.guardianService.getGuardian(guardianId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!guardianId
    }
  );
};

export const useGetGuardianDetails = ({
  guardianId
}: {
  guardianId: number;
}) => {
  return useQuery<Guardian, AxiosError<Error>>(
    ['get_guardian_details', guardianId],
    () => services.guardianService.getGuardianDetails(guardianId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!guardianId
    }
  );
};
